import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { GetGlycerinDisplayProductPayloadFragmentFragmentDoc } from '@packages/gql/generated/shopping/GetGlycerinDisplayProductPayloadFragmentFragmentDoc';
import { type GlycerinDisplayProductPayload } from '@packages/tracking';
import {
  getGlycerinArticleId,
  getGlycerinAvailability,
  getGlycerinServices,
  getGlycerinOrigin,
} from '@packages/modules/src/Tracking/helper';
import { getGlycerinPrice } from '../getGlycerinPrice';
import { getGlycerinRatingStatus } from '../getGlycerinRatingStatus';
import { getStockValuesFromDimensions } from '../getStockValuesFromDimensions';

/* GraphQL */ `
  fragment GetGlycerinDisplayProductPayloadFragment on Product {
    akl
    sku
    availability {
      ...GetGlycerinAvailabilityFragment
    }
    price {
      ...GetGlycerinPriceFragment
      ...GetGlycerinServicesCurrencyFragment
    }
    seoRatingGetGlycerinDisplayProductPayload: seoRating(locale: $locale) {
      ...GetGlycerinRatingStatusFragment
    }
    aplusGetGlycerinDisplayProductPayload: aplus(language: $lang) @skip(if: $skipAplus) {
      ...GetGlycerinServicesFragment
    }
    ...GetGlycerinArticleIdFragment
    ...GetStockValuesFromDimensionsFragment
  }
`;

export type GetGlycerinDisplayProductPayloadProps = {
  data: FragmentType<typeof GetGlycerinDisplayProductPayloadFragmentFragmentDoc>;
  refSearchParam: string | null;
};

export const getGlycerinDisplayProductPayload = ({
  data,
  refSearchParam,
}: GetGlycerinDisplayProductPayloadProps): GlycerinDisplayProductPayload => {
  const unmaskedData = getFragmentData(GetGlycerinDisplayProductPayloadFragmentFragmentDoc, data);
  const {
    akl,
    sku,
    price,
    availability,
    seoRatingGetGlycerinDisplayProductPayload,
    aplusGetGlycerinDisplayProductPayload,
  } = unmaskedData;

  const { inStock, outOfStock } = getStockValuesFromDimensions(unmaskedData);

  return {
    productId: akl,
    skuId: sku,
    variantsInStock: inStock,
    variantsOutOfStock: outOfStock,
    ...getGlycerinArticleId(unmaskedData),
    ...getGlycerinAvailability(availability),
    ...getGlycerinOrigin(refSearchParam),
    ...getGlycerinPrice(price),
    ...getGlycerinRatingStatus(seoRatingGetGlycerinDisplayProductPayload),
    ...getGlycerinServices({ aplus: aplusGetGlycerinDisplayProductPayload, price }),
  };
};
